import React, { useState, useEffect } from 'react';
import { subcontractorsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  HardHat,
  Plus,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  X,
  Save,
  Star,
  Grid,
  List,
  Filter,
} from 'lucide-react';

const SubContractors = () => {
  const { user } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [contractorTypes, setContractorTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showModal, setShowModal] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    contact_number: '',
    contact_email: '',
    contractor_type: '',
    address: '',
    rating: 5,
    notes: '',
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadContractors();
    loadContractorTypes();
  }, []);

  const loadContractors = async () => {
    try {
      const response = await subcontractorsAPI.getAll();
      setContractors(response.data);
    } catch (error) {
      toast.error('Failed to load sub contractors');
    } finally {
      setLoading(false);
    }
  };

  const loadContractorTypes = async () => {
    try {
      const response = await subcontractorsAPI.getTypes();
      setContractorTypes(response.data);
    } catch (error) {
      console.error('Failed to load contractor types');
    }
  };

  const handleOpenModal = (contractor = null) => {
    if (contractor) {
      setEditingContractor(contractor);
      setFormData({
        name: contractor.name || '',
        contact_person: contractor.contact_person || '',
        contact_number: contractor.contact_number || '',
        contact_email: contractor.contact_email || '',
        contractor_type: contractor.contractor_type || '',
        address: contractor.address || '',
        rating: contractor.rating || 5,
        notes: contractor.notes || '',
      });
    } else {
      setEditingContractor(null);
      setFormData({
        name: '',
        contact_person: '',
        contact_number: '',
        contact_email: '',
        contractor_type: '',
        address: '',
        rating: 5,
        notes: '',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingContractor) {
        await subcontractorsAPI.update(editingContractor.id, formData);
        toast.success('Sub contractor updated successfully');
      } else {
        await subcontractorsAPI.create(formData);
        toast.success('Sub contractor created successfully');
      }
      setShowModal(false);
      loadContractors();
    } catch (error) {
      toast.error(`Failed to ${editingContractor ? 'update' : 'create'} sub contractor`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sub contractor?')) return;
    
    try {
      await subcontractorsAPI.delete(id);
      toast.success('Sub contractor deleted successfully');
      loadContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sub contractor');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(10)].map((_, i) => (
          <Star
            key={i}
            className={`w-3 h-3 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
          />
        ))}
        <span className="ml-1 text-sm text-slate-600">({rating}/10)</span>
      </div>
    );
  };

  const filteredContractors = contractors.filter(contractor => {
    const matchesSearch = 
      contractor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contractor.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contractor.contractor_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || contractor.contractor_type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading sub contractors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sub Contractors
          </h1>
          <p className="text-slate-600 mt-1">
            Manage external contractors and service providers
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-5 h-5" />
            Add Sub Contractor
          </button>
        )}
      </div>

      {/* Search, Filter and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, contact person, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          />
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-9 pr-8 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
            >
              <option value="">All Types</option>
              {contractorTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <Grid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Count */}
      <p className="text-sm text-slate-600 mb-4">{filteredContractors.length} sub contractor(s) found</p>

      {/* Contractors Grid/List */}
      {filteredContractors.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <HardHat className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No sub contractors found</h3>
          <p className="text-slate-600 mb-4">Get started by adding your first sub contractor.</p>
          {canEdit && (
            <button
              onClick={() => handleOpenModal()}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Sub Contractor
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContractors.map((contractor) => (
            <div key={contractor.id} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <HardHat className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{contractor.name}</h3>
                    <span className="text-sm px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                      {contractor.contractor_type}
                    </span>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleOpenModal(contractor)}
                      className="p-1 hover:bg-slate-100 rounded transition-colors"
                    >
                      <Edit className="w-4 h-4 text-slate-500" />
                    </button>
                    <button
                      onClick={() => handleDelete(contractor.id)}
                      className="p-1 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="space-y-2 text-sm mb-3">
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="font-medium">Contact:</span>
                  <span>{contractor.contact_person}</span>
                </div>
                {contractor.contact_number && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4" />
                    <span>{contractor.contact_number}</span>
                  </div>
                )}
                {contractor.contact_email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{contractor.contact_email}</span>
                  </div>
                )}
                {contractor.address && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{contractor.address}</span>
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-slate-100">
                {renderStars(contractor.rating)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Company</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Contact Person</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Phone</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Rating</th>
                {canEdit && <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredContractors.map((contractor) => (
                <tr key={contractor.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <HardHat className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="font-medium text-slate-900">{contractor.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-sm">
                      {contractor.contractor_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{contractor.contact_person}</td>
                  <td className="px-4 py-3 text-slate-600">{contractor.contact_number}</td>
                  <td className="px-4 py-3">{renderStars(contractor.rating)}</td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleOpenModal(contractor)}
                          className="p-1 hover:bg-slate-100 rounded transition-colors"
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(contractor.id)}
                          className="p-1 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-slate-900">
                {editingContractor ? 'Edit Sub Contractor' : 'Add Sub Contractor'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Company Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <HardHat className="w-4 h-4" />
                  Company Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contractor Type *</label>
                    <select
                      required
                      value={formData.contractor_type}
                      onChange={(e) => setFormData({ ...formData, contractor_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select type...</option>
                      {contractorTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person *</label>
                    <input
                      type="text"
                      required
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contact Number *</label>
                    <input
                      type="tel"
                      required
                      value={formData.contact_number}
                      onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Rating
                </h3>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                    className="flex-1"
                  />
                  <div className="flex items-center gap-1">
                    {[...Array(10)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 cursor-pointer ${i < formData.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
                        onClick={() => setFormData({ ...formData, rating: i + 1 })}
                      />
                    ))}
                  </div>
                  <span className="text-lg font-semibold text-slate-700 w-12 text-center">{formData.rating}/10</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Any additional notes about this contractor..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {editingContractor ? 'Update' : 'Create'} Contractor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubContractors;
